import moment from 'moment';
import { add_acquisition_observables } from 'shared/classes/Acquisition';
import { SCEvent } from 'shared/classes/Event';
import { SCAdminTodo } from './Todo';
import { AdminPerson } from './Person';
import { AdminHost } from './Host';
import { Location } from 'shared/classes/Location';
import { AdminArtist } from './Artist';
import { Call } from './Call';

// email_sent_log.email_template_name values for the composer reminders,
// used to detect whether a reminder has already been sent for an event.
const ARTIST_REMINDER_TEMPLATE = 'artist_new_invite_reminder.html';
const HOST_REMINDER_TEMPLATE = 'host_reminder.html';

export class AdminEvent extends SCEvent {
  constructor(parameters) {
    super(parameters)

    this.type = 'AdminEvent'

    // Upgrade nested instances created by SCEvent (base) to Admin variants.
    // Strip back-references to break recursion through AdminPerson's events array.
    const stripPerson = (p) => p && { ...p, artist: undefined, host: undefined, events: undefined }
    this.host_or_creator_person = new AdminPerson(stripPerson(parameters.host_or_creator_person) ?? {})
    this.creator(new AdminPerson(stripPerson(parameters.creator) ?? {}))
    this.host_person(new AdminPerson(stripPerson(parameters.host_person) ?? {}))

    if (parameters.host) {
      this.host(new AdminHost({ ...parameters.host, person: undefined }))
    } else {
      this.host(new AdminHost({ host_uuid: parameters.host_uuid }))
    }

    if (parameters.artist) {
      this.artist(new AdminArtist({ ...parameters.artist, person: undefined }))
    } else {
      this.artist(new AdminArtist({ artist_uuid: parameters.artist_uuid }))
    }

    // First touch marketing channel of the event creator, the same data the
    // income table shows. Only some endpoints send it, so it can stay null.
    add_acquisition_observables(this, parameters.acquisition)

    this.emails= ko.observableArray(parameters.emails ?? []);

    this.rcmd_artists_sent_on = ko.observable(
      parameters.rcmd_artists_sent_on
        ? new Date(parameters.rcmd_artists_sent_on)
        : undefined
    );
    this.rcmd_artists_sent_by = ko.observable(parameters.rcmd_artists_sent_by ?? {});
    this.rcmd_artists = ko.observableArray(parameters.rcmd_artists ?? []);

    this.row_css = ko.pureComputed(() => {
      switch (this.current_state()?.event_state()) {
        case "waiting_for_approval":
          return "danger";
        case "waiting_for_confirmation":
          return "warning";
        case "no":
          return "";
        case "confirmed":
          return "success";
      }
    });

    // The latest artist-reminder email logged for this invite, if any.
    // emails() is ordered by inserted ASC, so the last match is the newest.
    this.artist_reminder_email = ko.pureComputed(() => {
      const matches = (this.emails() ?? []).filter(
        (email) => email?.email_template_name === ARTIST_REMINDER_TEMPLATE
      );
      return matches[matches.length - 1];
    });

    // True once an artist-reminder email has been logged for this invite.
    this.artist_reminder_sent = ko.pureComputed(
      () => this.artist_reminder_email() !== undefined
    );

    // When the artist reminder was sent, as a Date (undefined if never).
    this.artist_reminder_sent_on = ko.pureComputed(() => {
      const email = this.artist_reminder_email();
      return email ? new Date(email.inserted) : undefined;
    });

    // True if the artist has written at least one chat message on this invite,
    // i.e. reacted informally even without taking a formal event action.
    this.artist_reacted_in_chat = ko.pureComputed(() =>
      (this.messages() ?? []).some((message) => message?.actor?.() === 'artist')
    );
  }

  getType() {
      return this.type
  }

  async lookupDetails() {
    //console.log('Lookup all details');
    const url = this.rootvm.web_host + "/api/admin/event/details";
    try {
      const params = new URLSearchParams({ event_uuid: this.event_uuid() });
      const response = await fetch(`${url}?${params.toString()}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=utf-8"
        }
      });
      const data = await response.json();
      if (data.success === true) {
        // console.log('Got details', data);
        this.update_self(data.event, true);
        // update_self re-sets nested fields with base classes; upgrade to Admin variants
        this.artist(new AdminArtist({ ...data.event.artist, person: undefined }));
        if (data.event.host) {
          this.host(new AdminHost({ ...data.event.host, person: undefined }));
        }
        if (data.event.creator) {
          this.creator(new AdminPerson({ ...data.event.creator, artist: undefined, host: undefined, events: undefined }));
        }
        if (data.event?.calls) {
          this.calls([]);
          for (const call of data.event?.calls) {
            this.calls().push(new Call(call));
          }
        }
        // console.log(data.event.emails)
        this.emails(data.event.emails)
      }
    } catch (error) {
      console.log(error);
    }
  }
}

export function SCCombinedEvent(data) {
  const self = this;
  self.type = "SCCombinedEvent";

  self.rootvm = data.rootvm;

  self.event_types = ko.observable(data.event_types);
  self.name = ko.observable(data.name);
  self.ad_query = ko.observable(data.ad_query);
  self.admin_comment = ko.observable(data.admin_comment);
  self.email_address = ko.observable(data.email_address);
  self.phone = ko.observable(data.phone);
  self.recommendation_requested = ko.observable(data.recommendation_requested);
  self.match_requested = ko.observable(data.match_requested);
  self.most_recent = ko.observable(new moment(data.most_recent));
  self.creator_uuid = ko.observable(data.creator_uuid)

  self.sent_emails = data.sent_emails;

  self.average_fee = ko.observable(data.average_fee);

  if (self.average_fee()) {
    self.average_fee().toFixed(0);
  }
  self.invite_count = ko.observable(data.invite_count);

  self.first_approval = ko.observable(new moment(data.first_approval));

  self.actions = ko.observableArray(data.actions);


  self.possible_dates = ko.observableArray(data.possible_dates);

  self.location_names = ko.observable();
  self.location_names(data.location_names);

  self.location_json = data.location_json

  self.extra_notes = ko.observable(data.extra_notes);
  self.new_extra_notes = ko.observable(data.extra_notes);
  self.editing_extra_notes = ko.observable(false);
  self.admin_marked = ko.observable(false);

  self.display_sub_rows = ko.observable(true);

  self.invites = ko.observableArray([1, 2, 3]);
  self.invites(ko.utils.arrayMap(data.invites || [],
    function (x) {
      x.rootvm = self.rootvm
      return new AdminEvent(x);
    }));

  // The host reminder is logged against the first invite's event_uuid, so scan
  // every invite's emails for the template. Returns the latest match, if any.
  self.host_reminder_email = ko.pureComputed(function () {
    const matches = self.invites()
      .flatMap(function (invite) { return invite.emails() ?? []; })
      .filter(function (email) {
        return email?.email_template_name === HOST_REMINDER_TEMPLATE;
      })
      .sort(function (a, b) { return new Date(a.inserted) - new Date(b.inserted); });
    return matches[matches.length - 1];
  });

  // True once a host-reminder email has been logged for the combined event.
  self.host_reminder_sent = ko.pureComputed(function () {
    return self.host_reminder_email() !== undefined;
  });

  // When the host reminder was sent, as a Date (undefined if never).
  self.host_reminder_sent_on = ko.pureComputed(function () {
    const email = self.host_reminder_email();
    return email ? new Date(email.inserted) : undefined;
  });

  self.current_days_before_possible_date = ko.pureComputed(function () {
    if (self.possible_dates().length > 0) {
      return moment(self.possible_dates()[0]).diff(new moment(), 'days');
    }
    else {
      return '';
    }
  });

  self.host_login_link = ko.observable();
  self.get_host_login_link = function (scevent) {
    self.invites()[0].get_login_link("host").then(function () {
      self.host_login_link(self.invites()[0].host_login_link());
    });
  };

  self.todo_list = ko.observableArray([]);
  self.todo_list(ko.utils.arrayMap(data.todos || [],
    function (data) {
      const x = data[0];
      x.assigned_user_pretty = data[1];
      // This is a list and comes in like this:
      // [ TODO , Assigned Display Name]
      x.rootvm = self.rootvm
      return new SCAdminTodo(x);
    }));

  self.todo_list_not_complete = ko.pureComputed(function () {
    return ko.utils.arrayFilter(self.todo_list(), function (todo) {
      return todo.effective_upper() === undefined;
    });

  });

  self.todo_list_complete = ko.pureComputed(function () {
    return ko.utils.arrayFilter(self.todo_list(), function (todo) {
      return todo.effective_upper() !== undefined;
    });

  });
  self.is_closed = ko.pureComputed(function () {
    let closed = false;
    ko.utils.arrayForEach(self.invites(), function (invite) {
      if (invite.admin_closed()) {
        closed = true;
      }
    });
    return closed;
  })

  self.new_extra_notes = ko.observable(data.admin_comment) || '';
  self.extra_notes = ko.observable(data.admin_comment);
  self.editing_extra_notes = ko.observable(false);

  self.cancel_extra_notes = function () {
    self.new_extra_notes(self.admin_comment());
    self.editing_extra_notes(false);
  }

  self.save_extra_notes = function () {
    self.editing_extra_notes(false);

    return $.ajax({

      url: scvm.web_host + '/api/admin/people/admin-comment',
      type: 'POST',
      data: ko.toJSON({
        person_uuid: self.creator_uuid(),
        admin_comment: self.new_extra_notes()
      }),
      processData: false,

      contentType: "application/json; charset=utf-8",

      success: function (data) {

        if (data.success) {

          self.admin_comment(data.admin_comment);
          self.extra_notes(data.extra_notes);
          self.new_extra_notes(data.admin_comment);
          self.editing_extra_notes(false);
          notyf.success("Saved!");
        }
        else {
          notyf.error(data.message);
          self.editing_extra_notes(true);
        }

      },
      error: function (jqXHR, errorStatus, thrownError) {
        var response = jQuery.parseJSON(jqXHR.responseText);
        notyf.error(response.message);
      },
      complete: function () {
      }
    });
  };


}

export class SCEventCall {
  constructor(data) {
    var self = this;
    self.type = "SCEventCall";

    self.rootvm = data.rootvm;

    self.event_call_uuid = data.call_uuid;

    self.inserted = new moment(data.inserted);
    self.artist = data.artist ? new AdminArtist(data.artist) : null;

    self.call_state = data.call_state;

    self.twilio_audio_link = data.twilio_audio_link;

    if (data.host) {
      data.host.rootvm = self.rootvm;
    }

    self.host = new AdminHost(data.host || { rootvm: self.rootvm });
    self.creator = null;
    if (data.creator) {
      data.creator.rootvm = self.rootvm;
      self.creator = new AdminPerson(data.creator);
    }
    self.location = new Location(data.location);

    data.event.rootvm = self.rootvm;
    self.sc_event = data.event ? new AdminEvent(data.event) : null;

    self.participant_one_name = data.participant_one_name;
    self.participant_one_number = data.participant_one_number;
    self.participant_two_name = data.participant_two_name;
    self.participant_two_number = data.participant_two_number;

    self.possible_bypass_attempt = ko.observable(data.possible_bypass_attempt);
    self.call_summary = data.call_summary;
  }
}

